import * as React from 'react';
import { captureException } from '../utils/sentry/sentry-lazy';
import { LayoutPageErrorFallback } from '../components/LayoutPageErrorFallback';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** When set, render this instead of the default error UI when an error is caught. Use null to silently swallow. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Store component stack for display and persist for debug UI
    this.setState((s) => ({ ...s, componentStack: errorInfo.componentStack ?? undefined }));
    if (typeof window !== 'undefined') {
      (window as any).__lastErrorBoundaryComponentStack = errorInfo.componentStack ?? '';
      console.error('[ErrorBoundary] Caught error:', error.message);
      console.error('[ErrorBoundary] Error stack:', error.stack);
      console.error('[ErrorBoundary] React component stack (shows which component threw):', errorInfo.componentStack);
    }
    // Only capture in browser (Sentry may not be initialized during SSR)
    if (typeof window !== 'undefined') {
      captureException(error, {
        tags: {
          component: 'ErrorBoundary',
          errorType: 'react-error-boundary',
        },
        extra: {
          componentStack: errorInfo.componentStack,
        },
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback !== undefined) {
        return this.props.fallback;
      }
      // The design system's error state; the technical details (message, component stack) stay in the
      // console and in Sentry, and are only drawn on screen in development.
      return (
        <>
          <LayoutPageErrorFallback />
          {import.meta.env.DEV ? (
            <pre className="mx-auto max-w-3xl overflow-auto whitespace-pre-wrap break-all px-4 pb-8 text-xs text-jc-ink-secondary">
              {this.state.error?.toString()}
              {'\n'}
              {this.state.componentStack ?? ''}
            </pre>
          ) : null}
        </>
      );
    }

    return this.props.children;
  }
}
